// The org chooser. Once someone has picked an org, `/orgs` is no longer a place Back (or a
// generic "home" link) should land: the chooser is reached deliberately via "Switch
// organization" (`SWITCH_ORG_PATH`, `?switch=1`), and otherwise forwards to the active org's
// start page — its Home, `/o/:slug`. Every hop out of here uses history *replace*, so the
// chooser never sits in the back stack behind the org the user picked.
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../session";
import { SWITCH_ORG_PARAM, lastActiveOrg } from "../lib/lastOrg";
import { homePath } from "../library/routes";
import { AuthShell } from "../components/AuthShell";
import { PageLoader } from "../components/ui";
import { Avatar, Icon } from "@grackle/ui";

const roleLabel: Record<string, string> = {
  owner: "You are the owner",
  admin: "You are an admin",
  member: "You are a member",
};

export default function OrgChoice() {
  const { user, orgs, loading } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  // Read once: the flag is stripped from the URL below, but this visit stays a switch.
  const [switching] = useState(() => new URLSearchParams(location.search).get(SWITCH_ORG_PARAM) === "1");

  // Drop `?switch=1` from this history entry, so if the user leaves the chooser some other way
  // (e.g. "Settings") and later comes Back to it, that is a plain `/orgs` and forwards to
  // their start page rather than reopening the chooser.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has(SWITCH_ORG_PARAM)) return;
    params.delete(SWITCH_ORG_PARAM);
    const rest = params.toString();
    navigate({ pathname: location.pathname, search: rest ? `?${rest}` : "" }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  if (loading) {
    return <PageLoader />;
  }
  if (!user) {
    return <Navigate to="/signin" replace />;
  }
  const active = switching ? null : lastActiveOrg(orgs);
  if (active) {
    return <Navigate to={homePath(active.slug)} replace />;
  }

  return (
    <AuthShell
      title="Choose an organization"
      subtitle="Recordings live inside an organization. You can belong to more than one."
      footer={
        <>
          Signed in as {user.email} ·{" "}
          <Link to="/account" replace>
            Settings
          </Link>
        </>
      }
    >
      <div className="choice-list">
        {orgs.length > 0 ? (
          <>
            <div className="gk-overline">Your organizations</div>
            {orgs.map((org) => (
              <Link
                key={org.id}
                to={homePath(org.slug)}
                replace
                className="gk-card gk-card--sm gk-card--interactive choice-row"
              >
                <Avatar
                  square
                  size={32}
                  initials={org.name.trim().slice(0, 1).toUpperCase()}
                  color="var(--surface-inverse)"
                  style={{ color: "var(--now)", fontSize: 14 }}
                  aria-hidden
                />
                <div className="choice-body">
                  <div className="choice-title">{org.name}</div>
                  <div className="choice-note">{roleLabel[org.role] ?? org.role}</div>
                </div>
                <span className="gk-btn gk-btn--primary gk-btn--sm choice-cta">Open</span>
              </Link>
            ))}
          </>
        ) : (
          <div className="choice-note">You don't belong to an organization yet.</div>
        )}

        <div className="gk-overline">Or</div>
        <Link to="/orgs/new" replace className="gk-card gk-card--sm gk-card--interactive choice-row">
          <div className="choice-icon">
            <Icon name="plus" size={18} />
          </div>
          <div className="choice-body">
            <div className="choice-title">Create a new organization</div>
            <div className="choice-note">You become its owner</div>
          </div>
        </Link>
      </div>
    </AuthShell>
  );
}
